import axios from 'axios';
import type { Task, Board, Sprint, Team, User, Label, Comment, Attachment, SubTask, BoardColumn, TaskStatus } from '../types';

const api = axios;

// Interceptor to inject auth token if present
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers['x-access-token'] = token;
    }
    return config;
});

// Generic response handler
function handleResponse<T>(res: { data: { success: boolean; data: T; error?: string } }): T {
    if (!res.data.success) throw new Error(res.data.error || 'API Error');
    return res.data.data;
}

// =====================================================================
// Labels
// =====================================================================
export async function fetchLabels(): Promise<Label[]> {
    const res = await api.get('/tm/labels');
    return handleResponse(res);
}

export async function createLabel(label: Omit<Label, 'id'>): Promise<Label> {
    const res = await api.post('/tm/labels', label);
    return handleResponse(res);
}

export async function updateLabel(label: Label): Promise<boolean> {
    const res = await api.put('/tm/labels', { id: label.id, name: label.name, color: label.color });
    return handleResponse(res);
}

export async function deleteLabel(id: string): Promise<boolean> {
    const res = await api.delete('/tm/labels', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// Users
// =====================================================================
export async function fetchUsers(): Promise<User[]> {
    const res = await api.get('/tm/users');
    return handleResponse(res);
}

export async function updateUserRole(userId: string, role: string): Promise<boolean> {
    const res = await api.put('/tm/users/role', { userId, role });
    return handleResponse(res);
}

export async function updateUserAvatar(userId: string, avatarColor: string): Promise<boolean> {
    const res = await api.put('/tm/users/avatar', { userId, avatarColor });
    return handleResponse(res);
}

// =====================================================================
// Teams
// =====================================================================
export async function fetchTeams(): Promise<Team[]> {
    const res = await api.get('/tm/teams');
    return handleResponse(res);
}

export async function createTeam(team: Omit<Team, 'id' | 'createdAt'>): Promise<Team> {
    const res = await api.post('/tm/teams', team);
    return handleResponse<{ data: Team }>(res) as unknown as Team;
}

export async function updateTeam(team: Team): Promise<Team> {
    const { id, ...rest } = team;
    const res = await api.put('/tm/teams', { id, ...rest });
    return handleResponse<{ data: Team }>(res) as unknown as Team;
}

export async function deleteTeam(id: string): Promise<boolean> {
    const res = await api.delete('/tm/teams', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// Boards
// =====================================================================
export async function fetchBoards(): Promise<Board[]> {
    const res = await api.get('/tm/boards');
    return handleResponse(res);
}

export async function createBoard(board: Omit<Board, 'id' | 'createdAt'>): Promise<Board> {
    const res = await api.post('/tm/boards', board);
    return handleResponse<{ data: Board }>(res) as unknown as Board;
}

export async function updateBoard(board: Partial<Board> & { id: string }): Promise<Board> {
    const res = await api.put('/tm/boards', board);
    return handleResponse<{ data: Board }>(res) as unknown as Board;
}

export async function deleteBoard(id: string): Promise<boolean> {
    const res = await api.delete('/tm/boards', { data: { id } });
    return handleResponse(res);
}

export async function updateBoardColumns(boardId: string, columns: BoardColumn[]): Promise<Board> {
    const res = await api.put('/tm/boards/columns', { boardId, columns });
    return handleResponse<{ data: Board }>(res) as unknown as Board;
}

// =====================================================================
// Sprints
// =====================================================================
export async function fetchSprints(boardId?: string): Promise<Sprint[]> {
    const res = await api.get('/tm/sprints', { params: boardId ? { boardId } : {} });
    return handleResponse(res);
}

export async function createSprint(sprint: Omit<Sprint, 'id'>): Promise<Sprint> {
    const res = await api.post('/tm/sprints', sprint);
    return handleResponse<{ data: Sprint }>(res) as unknown as Sprint;
}

export async function updateSprint(sprint: Partial<Sprint> & { id: string }): Promise<Sprint> {
    const res = await api.put('/tm/sprints', sprint);
    return handleResponse<{ data: Sprint }>(res) as unknown as Sprint;
}

export async function deleteSprint(id: string): Promise<boolean> {
    const res = await api.delete('/tm/sprints', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// Tasks
// =====================================================================
export async function fetchTasks(params?: { boardId?: string; sprintId?: string; status?: string; assigneeId?: string; search?: string }): Promise<Task[]> {
    const res = await api.get('/tm/tasks', { params });
    return handleResponse(res);
}

export async function fetchTask(guid: string): Promise<Task> {
    const res = await api.get(`/tm/tasks/${guid}`);
    return handleResponse(res);
}

export async function createTask(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<Task> {
    const res = await api.post('/tm/tasks', task);
    return handleResponse<{ data: Task }>(res) as unknown as Task;
}

export async function updateTask(task: Partial<Task> & { id: string }): Promise<Task> {
    const res = await api.put('/tm/tasks', task);
    return handleResponse<{ data: Task }>(res) as unknown as Task;
}

export async function deleteTask(id: string): Promise<boolean> {
    const res = await api.delete('/tm/tasks', { data: { id } });
    return handleResponse(res);
}

export async function moveTask(taskId: string, status: TaskStatus): Promise<boolean> {
    const res = await api.put('/tm/tasks/move', { taskId, status });
    return handleResponse(res);
}

// Comments
export async function addComment(taskId: string, content: string, attachments?: { name: string; size: number; type: string; url: string }[]): Promise<Comment> {
    const res = await api.post('/tm/tasks/comment', { taskId, content, attachments });
    return handleResponse(res);
}

// Attachments
export async function addAttachment(taskId: string, attachment: { name: string; size: number; type: string; url: string }): Promise<Attachment> {
    const res = await api.post('/tm/tasks/attachment', { taskId, ...attachment });
    return handleResponse(res);
}

export async function removeAttachment(id: string): Promise<boolean> {
    const res = await api.delete('/tm/tasks/attachment', { data: { id } });
    return handleResponse(res);
}

// Subtasks
export async function addSubtask(taskId: string, subtask: Omit<SubTask, 'id'>): Promise<SubTask> {
    const res = await api.post('/tm/tasks/subtask', { taskId, ...subtask });
    return handleResponse(res);
}

export async function updateSubtask(subtask: Partial<SubTask> & { id: string }): Promise<boolean> {
    const res = await api.put('/tm/tasks/subtask', subtask);
    return handleResponse(res);
}

export async function deleteSubtask(id: string): Promise<boolean> {
    const res = await api.delete('/tm/tasks/subtask', { data: { id } });
    return handleResponse(res);
}

// =====================================================================
// File uploads (via UploadController)
// =====================================================================
export async function uploadFile(file: File): Promise<{ path: string; id_upload: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post('/upload/file', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-access-token': localStorage.getItem('token') || '',
        },
    });
    if (!res.data.success) throw new Error(res.data.error || 'Upload failed');
    return res.data.data;
}

// Assign / unassign a task to a sprint
export async function assignTaskToSprint(taskId: string, sprintId: string | null): Promise<boolean> {
    const res = await api.put('/tm/tasks', { id: taskId, sprintId });
    if (!res.data.success) throw new Error(res.data.error || 'API Error');
    return true;
}

export default api;
